import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router";
import { useApi } from "~hooks/apiHooks/useApi";
import { useGetLocationParams } from "~hooks/useGetLocationParams.ts";
import type { PermissionScopeResponse } from "~types/permissionScope";

const useGetCurrentUserPermissionScope = () => {
  const request = useApi();
  const { customerId, sport, organizationId } = useGetLocationParams();
  const navigate = useNavigate();
  const location = useLocation();

  return useQuery({
    queryKey: ["permission-scope-current-user", sport, organizationId],
    queryFn: () => {
      const url = `${sport}/o/${organizationId}/user/scope`;
      const url403 = `/${customerId}/admin/${sport}/${organizationId}/403`;
      const urlScopeError = `/${customerId}/admin/${sport}/${organizationId}/scope-error`;

      return request<PermissionScopeResponse>(url, {
        method: "GET",
        onError: {
          403: () => {
            if (!location.pathname.endsWith(url403)) {
              navigate(url403, { state: { back: `/${customerId}/select/organization` } });
            }
          },
          default: (response: Response, data: unknown) => {
            if (!location.pathname.endsWith(urlScopeError)) {
              navigate(urlScopeError, { state: { statusCode: response.status, error: JSON.stringify(data) } });
            }
          },
        }
      }).then((response: PermissionScopeResponse) => ({
        scope: response?.data?.[0] || {},
        resources: response?.includes?.resources || {},
      }))
    },
    staleTime: () => 5 * 60 * 1000,
    enabled: !!organizationId && !!sport,
    retry: (failureCount: number, response: any) => {
      if (response?.errors?.code === 403) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export default useGetCurrentUserPermissionScope;
